import * as Yup from 'yup';
import {string} from 'yup';
import {ID, IModel} from '~/lib/model';
import {AccountCondition, AccountPaymentType, defaultTakeRateCap, ITakeRateCap, SellerType, TakeRateCapType} from '~/modules/Clients/models/AccountInfo';
import {Configuration, ConfigurationSchema, defaultConfiguration} from '~/modules/Clients/models/Configuration';

export type Mediation = string;
export type IntegrationStatus = string;
export type ClientType = string;
export type DataCube = string;
export type Country = string;

export enum Region {
    APAC = 'APAC',
    NA = 'North America',
    SA = 'South America',
    EMEA = 'EMEA',
    China = 'China'
}

export const sellerRegions = Object.keys(Region).map(key => Region[key]);

export interface ISellerShort {
    id: number;
    name: string;
    active: boolean;
    sellerType: 'self_serve' | 'internal';
    accountManagerId?: number;
}

export interface ISellerPreview {
    id: ID;
    name?: string;
}

export interface ISeller extends IModel {
    name: string;
    active: boolean;
    bcat?: string[];
    bapp?: string[];
    badv?: string[];
    configuration: Configuration;
    sellerType: string;
    paymentType: string;
    conditions: AccountCondition;
    salesRepresentativeId: number;
    accountManagerId: number;
    mediation?: Mediation;
    integrationStatus?: IntegrationStatus;
    clientType?: ClientType;
    dataCubes: DataCube[];
    expectedUsDau?: number;
    expectedUsSpend?: number;
    mediationDashboardNames: string[];
    minPaymentsBalance: number;
    country?: Country;
    region: Region;
    companyEmail?: string;
    takeRateCap: ITakeRateCap;
    presetId?: number | null;
    attributes?: ISellerAttributes | null;
}

export interface ISellerAttributes {
    reportingApiUrl?: string | null;
    accessToken?: string | null;
    accessUsername?: string | null;
    accessPassword?: string | null;
    publisherId?: string | null;
}

export const SellerSchema = Yup.object().shape<ISeller>({
    id: Yup.number().nullable(),
    name: Yup.string().required(),
    active: Yup.boolean().nullable(),
    configuration: ConfigurationSchema('configuration'),
    sellerType: Yup.mixed<SellerType>().oneOf(Object.values(SellerType)).required(),
    paymentType: Yup.mixed<AccountPaymentType>().oneOf(Object.values(AccountPaymentType)).required(),
    conditions: Yup.mixed<AccountCondition>().oneOf(Object.values(AccountCondition)).required(),
    salesRepresentativeId: Yup.number().nullable(),
    accountManagerId: Yup.number().nullable(),
    dataCubes: Yup.array<DataCube>().nullable(),
    expectedUsDau: Yup.number().nullable(),
    expectedUsSpend: Yup.number().nullable(),
    mediationDashboardNames: Yup.array().of(Yup.string()
        .matches(/^[a-zA-Z0-9\s!@#$%^_&*()\-=+\[\]{};:'",<>.?/\\|`~]+$/, 'Each element must contain only Latin letters, numbers, spaces and special characters.')
        .max(50)
        .required()
    ).nullable(),
    minPaymentsBalance: Yup.number().required(),
    country: Yup.string().nullable(),
    region: Yup.mixed<Region>().nullable(),
    companyEmail: Yup.string().email('Invalid email format (e.g. seller@company.com)').nullable(),
    presetId: Yup.number().nullable().required('Preset is required'),
    takeRateCap: Yup.object().shape<ITakeRateCap>({
        type: Yup.mixed<TakeRateCapType>().oneOf(Object.values(TakeRateCapType)).required(),
        value: Yup.number().when('type', {
            is: (type: TakeRateCapType) => type && type !== TakeRateCapType.DISABLED,
            then: Yup.number()
                .typeError('Take rate cap is required')
                .min(0, 'Must be greater than or equal to 0')
                .lessThan(100, 'Must be less than 100')
                .required('Take rate cap is required'),
            otherwise: Yup.number().nullable()
        })
    }).required(),
    attributes: Yup.object().shape<ISellerAttributes>({
        reportingApiUrl: Yup.string().nullable(),
        accessToken: Yup.string().nullable(),
        accessUsername: Yup.string().nullable(),
        accessPassword: Yup.string().nullable(),
        publisherId: Yup.string().nullable(),
    }).nullable()
});

export const defaultSeller: ISeller = {
    id: null,
    name: '',
    active: false,
    configuration: defaultConfiguration,
    sellerType: 'internal',
    paymentType: 'internal',
    conditions: AccountCondition.NET60,
    salesRepresentativeId: null,
    accountManagerId: null,
    dataCubes: ['clicks', 'impressions'],
    minPaymentsBalance: 1000,
    mediationDashboardNames: [],
    country: null,
    region: null,
    takeRateCap: defaultTakeRateCap,
    presetId: null
};

const emptyToNull = (value?: string | null): string | null => value?.trim() ? value : null;

const withAttributesNulls = (seller: ISeller): ISeller =>
    seller.attributes
        ? {
            ...seller,
            attributes: {
                reportingApiUrl: emptyToNull(seller.attributes.reportingApiUrl),
                accessToken: emptyToNull(seller.attributes.accessToken),
                accessUsername: emptyToNull(seller.attributes.accessUsername),
                accessPassword: emptyToNull(seller.attributes.accessPassword),
                publisherId: emptyToNull(seller.attributes.publisherId),
            }
        }
        : seller;

export const onSubmitPreHandle = (seller: ISeller): ISeller => {
    const normalized = withAttributesNulls(seller);
    if (normalized.takeRateCap.type === TakeRateCapType.DISABLED) {
        return {
            ...normalized,
            takeRateCap: defaultTakeRateCap
        };
    }
    return normalized;
};
